import { Build } from '@stencil/core';

/**
 * Directus backend selection.
 *
 * Default (live site, prerendering, every non-dev host): the live backend.
 * On dev hosts the backend can be switched via URL query, the choice is remembered in localStorage:
 *
 *   ?backend=local   -> local Directus (https://admin-local.mammo-programm.de)
 *   ?backend=live    -> live Directus (default)
 *   ?backend=<url>   -> any Directus, e.g. ?backend=http://localhost:8055
 *   ?assets=live     -> images/downloads from live, even while using the local backend
 *   ?assets=local|<url>, ?assets=auto (= follow backend)
 */

export const LIVE_URL = 'https://admin.mammo-programm.de';
export const LOCAL_URL = 'https://admin-local.mammo-programm.de';

const DEV_HOSTS = ['localhost', '127.0.0.1', '192.168.2.102'];
const STORAGE_KEY_BACKEND = 'mp-backend';
const STORAGE_KEY_ASSETS = 'mp-assets';


function isDevHost(): boolean {

  return Build.isBrowser && typeof window !== 'undefined' && DEV_HOSTS.includes(window.location.hostname);
}

function resolveUrl(value: string): string {

  if (!value || value == 'live') return LIVE_URL;
  if (value == 'local') return LOCAL_URL;
  if (/^https?:\/\/[^\s]+$/.test(value)) return value.replace(/\/+$/, '');

  return LIVE_URL;
}

function readSetting(param: string, storage_key: string): string {

  try {
    const value = new URLSearchParams(window.location.search).get(param);

    if (value == 'auto') localStorage.removeItem(storage_key);
    else if (value) localStorage.setItem(storage_key, value);

    return localStorage.getItem(storage_key);
  } catch (e) {
    return null;
  }
}

function showBadge(backend: string, assets: string) {

  const add = () => {
    const badge = document.createElement('div');
    badge.textContent = `BACKEND: ${backend}` + (assets != backend ? ` | ASSETS: ${assets}` : '');
    badge.title = 'Switch back with ?backend=live';
    badge.setAttribute('style', 'position:fixed;left:8px;bottom:8px;z-index:100000;padding:4px 8px;border-radius:4px;' +
      'background:#c00;color:#fff;font:bold 11px/1.4 monospace;pointer-events:none;opacity:.85');
    document.body.appendChild(badge);
  };

  if (document.body) add();
  else document.addEventListener('DOMContentLoaded', add);
}


let backend = LIVE_URL;
let assets = LIVE_URL;

if (isDevHost()) {
  const backend_setting = readSetting('backend', STORAGE_KEY_BACKEND);
  const assets_setting = readSetting('assets', STORAGE_KEY_ASSETS);

  backend = resolveUrl(backend_setting);
  assets = assets_setting ? resolveUrl(assets_setting) : backend;

  if (backend != LIVE_URL || assets != LIVE_URL) {
    console.info(`[mp] Directus backend: ${backend}, assets: ${assets}`);
    showBadge(backend, assets);
  }
}

export const BACKEND_URL = backend;
export const GRAPHQL_URL = `${backend}/graphql`;
export const ASSETS_URL = assets;

/** Rewrites an absolute live asset URL (e.g. images inside rich text) to the active assets backend. */
export function toAssetsUrl(url: string): string {

  return url && url.startsWith(LIVE_URL) ? ASSETS_URL + url.substring(LIVE_URL.length) : url;
}
